import type {
  CustomAgentConfigStatus,
  CustomAgentTrigger,
  TriggerEvalResult,
  TriggerStateRow
} from '@axilon-apps/domains-ontology-ui-state'

import { deriveTriggerDisplayStatuses } from '../../../components/triggers/TriggerRulesEditor'

export type CustomAgentLiveKind =
  | 'gated_unreviewed_findings'
  | 'triggering'
  | 'condition_met_inactive'
  | 'healthy'
  | 'checking'
  | 'unavailable'
  | 'no_triggers'

export interface CustomAgentLiveSummary {
  kind: CustomAgentLiveKind
  activeTriggerCount?: number
  activeTriggerLabels?: string[]
  unreviewedFindingsCount?: number
}

export function deriveCustomAgentLiveSummary(input: {
  triggers: CustomAgentTrigger[]
  evalByTriggerId?: Map<string, TriggerEvalResult>
  storedTriggerStates?: TriggerStateRow[]
  configState: CustomAgentConfigStatus
  evalLoading: boolean
  evalError: boolean
  hasEvaluation: boolean
  gateOnUnreviewedFindings?: boolean
  unreviewedFindingsCount?: number
}): CustomAgentLiveSummary {
  const {
    triggers,
    evalByTriggerId,
    storedTriggerStates,
    configState,
    evalLoading,
    evalError,
    hasEvaluation,
    gateOnUnreviewedFindings,
    unreviewedFindingsCount = 0
  } = input

  const gated =
    configState === 'active' && !!gateOnUnreviewedFindings && unreviewedFindingsCount > 0
  const gatedSummary = (): CustomAgentLiveSummary => ({
    kind: 'gated_unreviewed_findings',
    unreviewedFindingsCount
  })

  if (triggers.length === 0) {
    if (gated) return gatedSummary()
    return { kind: 'no_triggers' }
  }
  if (!hasEvaluation && storedTriggerStates === undefined) {
    if (gated) return gatedSummary()
    if (evalLoading) return { kind: 'checking' }
    if (evalError) return { kind: 'unavailable' }
    return { kind: 'checking' }
  }

  const displayStatuses = deriveTriggerDisplayStatuses(triggers, evalByTriggerId)
  const storedById = new Map(storedTriggerStates?.map((state) => [state.trigger_id, state]) ?? [])
  const activeIndexes = triggers.flatMap((trigger, index) => {
    if (storedTriggerStates !== undefined) {
      const phase = trigger.id ? storedById.get(trigger.id)?.phase : undefined
      return phase === 'active' || phase === 'pending_off' ? [index] : []
    }
    return displayStatuses[index] === 'firing' ? [index] : []
  })
  if (activeIndexes.length > 0) {
    const activeTriggerLabels = activeIndexes.map(
      (index) => triggers[index].name?.trim() || `Trigger ${index + 1}`
    )
    const base = { activeTriggerCount: activeIndexes.length, activeTriggerLabels }
    if (configState !== 'active') {
      return { kind: 'condition_met_inactive', ...base }
    }
    if (gated) {
      return { ...gatedSummary(), ...base }
    }
    return { kind: 'triggering', ...base }
  }

  if (gated) return gatedSummary()

  if (
    storedTriggerStates?.some(
      (state) => state.phase === 'pending_on' || state.phase === 'pending_off'
    )
  ) {
    return { kind: 'checking' }
  }
  if (
    storedTriggerStates !== undefined &&
    triggers.every((trigger) => trigger.id && storedById.get(trigger.id)?.phase === 'inactive')
  ) {
    return { kind: 'healthy' }
  }
  const allHealthy = displayStatuses.length > 0 && displayStatuses.every((s) => s === 'healthy')
  if (allHealthy) return { kind: 'healthy' }
  if (evalError) return { kind: 'unavailable' }
  return { kind: 'unavailable' }
}
